import { Box, Button, useToast } from '@chakra-ui/react';
import { AddOutlineIcon } from '@diamond/shared/icons';
import { showToast } from '@diamond/shared/ui';
import React, { useCallback, useImperativeHandle, useState } from 'react';

import { useDeleteProductHighlight } from '../hooks';
import { HighlightCreateModal } from './highlight-create-modal';
import { HighlightDeleteModal } from './highlight-delete-modal';

interface HighlightModalContainerProps {
  allProductHighlightData: Array<{
    id: string;
    title: string;
    product_highlight_type: 'general' | 'specific' | undefined;
    total_business_types: number;
    total_shiptos: number;
  }>;
  refetch: () => void;
}

export interface HighlightModalHandlers {
  handleOpenEdit: (id: string) => void;
  handleOpenDelete: (id: string) => void;
  handleOpenDuplicate: (id: string) => void;
}

export const HighlightModalContainer = React.forwardRef<
  HighlightModalHandlers,
  HighlightModalContainerProps
>(function HighlightModalContainer({ allProductHighlightData, refetch }, ref) {
  const toast = useToast();

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [duplicateId, setDuplicateId] = useState<string | null>(null);

  const deleteHighlightHook = useDeleteProductHighlight();

  const handleOpenCreate = useCallback(() => {
    setCreateModalOpen(true);
  }, []);

  const handleOpenEdit = useCallback((id: string) => {
    setEditId(id);
    setEditModalOpen(true);
  }, []);

  const handleOpenDelete = useCallback((id: string) => {
    setDeleteId(id);
    setDeleteModalOpen(true);
  }, []);

  const handleOpenDuplicate = useCallback((id: string) => {
    setDuplicateId(id);
    setDuplicateModalOpen(true);
  }, []);

  const handleCloseCreate = useCallback(() => {
    setCreateModalOpen(false);
  }, []);

  const handleCloseEdit = useCallback(() => {
    setEditModalOpen(false);
    setEditId(null);
  }, []);

  const handleCloseDelete = useCallback(() => {
    setDeleteModalOpen(false);
    setDeleteId(null);
  }, []);

  const handleCloseDuplicate = useCallback(() => {
    setDuplicateModalOpen(false);
    setDuplicateId(null);
  }, []);

  const handleSubmitDelete = () => {
    if (deleteId) {
      deleteHighlightHook
        .mutateAsync(deleteId)
        .then(handleDeleteSuccess)
        .catch((err: Error) => {
          showToast(toast, 'error', err.message);
        });
    }
  };

  const handleDeleteSuccess = useCallback(() => {
    refetch();
    showToast(toast, 'success', 'Produk Highlight Anda berhasil dihapus!');
    handleCloseDelete();
  }, [refetch, toast, handleCloseDelete]);

  useImperativeHandle(ref, () => ({
    handleOpenEdit,
    handleOpenDelete,
    handleOpenDuplicate,
  }));

  return (
    <>
      <Box my={4} display="flex" justifyContent="flex-end">
        <Button
          leftIcon={<AddOutlineIcon boxSize={4} />}
          bg="blue"
          color="white"
          variant="solid"
          _hover={{ bg: 'navy' }}
          onClick={handleOpenCreate}
        >
          Tambah Highlight
        </Button>
      </Box>

      <HighlightCreateModal
        isOpen={createModalOpen}
        modalState="create"
        id=""
        allProductHighlightData={allProductHighlightData}
        onClose={handleCloseCreate}
        refetch={refetch}
      />

      <HighlightCreateModal
        isOpen={editModalOpen}
        modalState="edit"
        id={editId || ''}
        allProductHighlightData={allProductHighlightData}
        onClose={handleCloseEdit}
        refetch={refetch}
      />

      <HighlightCreateModal
        isOpen={duplicateModalOpen}
        modalState="duplicate"
        id={duplicateId || ''}
        allProductHighlightData={allProductHighlightData}
        onClose={handleCloseDuplicate}
        refetch={refetch}
      />

      <HighlightDeleteModal
        isOpen={deleteModalOpen}
        onClose={handleCloseDelete}
        onSubmit={handleSubmitDelete}
      />
    </>
  );
});
